import React, { useState } from "react";
import { Upload, Download, FileSpreadsheet, Lock, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useSafeBackdropClose } from "../lib/modal";

const STATUS_STYLE = {
  fillable: { bg: "#DCFCE7", fg: "#166534", label: "Will fill", icon: Check },
  locked:   { bg: "#FEF3C7", fg: "#92400E", label: "Locked", icon: Lock },
  same:     { bg: "#E5E7EB", fg: "#374151", label: "Matches", icon: Check },
  blank:    { bg: "#F3F4F6", fg: "#6B7280", label: "No value", icon: X },
};

/**
 * EnrichFromExcelDialog — per-project import flow that fills ONLY the
 * fields currently empty in-app. In-app data always wins. User picks
 * exactly which fillable fields to apply via checkboxes. Property code
 * and Unit code are the headline use-case but any supported field works.
 */
export default function EnrichFromExcelDialog({ project, onClose, onApplied }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { preview[], values, source_filename, parse_errors[], fillable_count }
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const backdrop = useSafeBackdropClose(onClose);

  const downloadTemplate = () => {
    const url = `/api/projects/${project.id}/enrich-template.xlsx`;
    const a = document.createElement("a");
    a.href = url; a.download = "";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/projects/${project.id}/enrich-from-excel`, fd,
      );
      setPreview(data);
      // Pre-select every fillable row — one click applies the batch.
      const picks = new Set(data.preview.filter(r => r.selected).map(r => r.field));
      setSelected(picks);
      if (data.fillable_count === 0) {
        toast.message("Nothing to fill — every in-app field is already set.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not read the uploaded file.");
    } finally {
      setUploading(false);
      e.target.value = ""; // allow re-upload of same file
    }
  };

  const toggle = (field) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  };

  const apply = async () => {
    if (selected.size === 0) {
      toast.error("Tick at least one field to apply.");
      return;
    }
    setApplying(true);
    try {
      const fields = [...selected];
      const { data } = await api.post(
        `/projects/${project.id}/enrich-from-excel/commit`,
        { fields, values: preview.values, source_filename: preview.source_filename },
      );
      toast.success(`${data.applied_count} field${data.applied_count === 1 ? "" : "s"} filled from Excel.`);
      onApplied && onApplied(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not apply the enrichment.");
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <div className="modal-overlay" {...backdrop} data-testid="enrich-dialog">
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label flex items-center gap-2">
              <FileSpreadsheet size={11} /> Enrich from Excel
            </div>
            <h2 className="font-display text-xl font-semibold mt-1">Fill missing fields from a spreadsheet</h2>
            <p className="muted text-xs mt-1">
              In-app values are <strong>never overwritten</strong>. Only fields that are currently
              empty on this project can be filled, and you choose which ones.
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="enrich-close-btn">
            <X size={14} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button onClick={downloadTemplate} className="btn-ghost text-xs"
                  data-testid="enrich-download-template"
                  title="Download a one-row template pre-filled with this project's current values.">
            <Download size={13} /> Download template
          </button>
          <label className="btn-primary text-xs cursor-pointer" data-testid="enrich-upload-label">
            <Upload size={13} /> {uploading ? "Reading…" : "Upload .xlsx / .csv"}
            <input type="file" accept=".xlsx,.xlsm,.csv" className="hidden"
                   onChange={onFile}
                   disabled={uploading}
                   data-testid="enrich-file-input" />
          </label>
          {preview && (
            <span className="text-xs muted">
              Source: <strong>{preview.source_filename || "upload"}</strong>
              {" · "}fillable {preview.fillable_count}
            </span>
          )}
        </div>

        {preview?.parse_errors?.length > 0 && (
          <div className="mb-3 border border-amber-300 bg-amber-50 text-amber-900 text-xs p-3 rounded"
               data-testid="enrich-parse-errors">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle size={13} /> Some cells could not be parsed
            </div>
            <ul className="mt-1 list-disc list-inside">
              {preview.parse_errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {preview && (
          <>
            <div className="overflow-y-auto border border-[var(--line)] rounded"
                 style={{ maxHeight: "50vh" }}
                 data-testid="enrich-preview-table">
              <table className="w-full text-xs">
                <thead className="bg-white shadow-[0_1px_0_var(--line)] sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-2 py-2 w-10"></th>
                    <th className="px-2 py-2">Field</th>
                    <th className="px-2 py-2">Current value</th>
                    <th className="px-2 py-2">From spreadsheet</th>
                    <th className="px-2 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row) => {
                    const meta = STATUS_STYLE[row.status] || STATUS_STYLE.blank;
                    const Icon = meta.icon;
                    const disabled = row.status !== "fillable";
                    return (
                      <tr key={row.field}
                          className={disabled ? "opacity-70" : "hover:bg-[var(--surface-2,#F6F7F9)]"}
                          data-testid={`enrich-row-${row.field}`}
                          data-status={row.status}>
                        <td className="px-2 py-1.5">
                          <input type="checkbox"
                                 checked={selected.has(row.field)}
                                 disabled={disabled}
                                 onChange={() => toggle(row.field)}
                                 data-testid={`enrich-check-${row.field}`} />
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{row.field}</td>
                        <td className="px-2 py-1.5 muted">
                          {row.current === null || row.current === "" || row.current === undefined
                            ? <em className="muted">— empty —</em>
                            : String(row.current)}
                        </td>
                        <td className="px-2 py-1.5">
                          {row.incoming === null || row.incoming === "" || row.incoming === undefined
                            ? <em className="muted">—</em>
                            : <strong>{String(row.incoming)}</strong>}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded"
                                style={{ background: meta.bg, color: meta.fg }}
                                title={row.reason}>
                            <Icon size={10} /> {meta.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={onClose} className="btn-ghost" data-testid="enrich-cancel-btn">
                Cancel
              </button>
              <button onClick={apply} disabled={applying || selectedCount === 0}
                      className="btn-primary"
                      data-testid="enrich-apply-btn">
                <Check size={13} /> {applying ? "Applying…" : `Apply ${selectedCount} field${selectedCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
